import { NgClass, NgStyle } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DataStore } from '@compiere-ws/models/compiere-data-json';
import EditTabUiComponent from '@iupics-components/standard/layouts/edit-tab-ui/edit-tab-ui.component';
import { AbstractDataContainer, AbstractDataContainerCallout } from '@iupics-manager/models/abstract-datacontainer';
import { IupicsDataField } from '@iupics-manager/models/iupics-data';
import { TranslateModule } from '@ngx-translate/core';
import { isNil } from 'lodash';
import { TooltipModule } from 'primeng/tooltip';
import Quill from 'quill';
import { LabelUiComponent } from '../label-ui/label-ui.component';

@Component({
  selector: 'iu-input-text-html-ui',
  templateUrl: './input-text-html-ui.component.html',
  styleUrls: ['./input-text-html-ui.component.scss'],
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [TooltipModule, NgClass, NgStyle, FormsModule, LabelUiComponent, TranslateModule],
})
export default class InputTextHtmlUiComponent
  extends AbstractDataContainer
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild('quillHeader', { read: ElementRef, static: true })
  header: ElementRef<HTMLDivElement>;
  @ViewChild('quillEditor', { read: ElementRef, static: true })
  editor: ElementRef<HTMLDivElement>;
  @Input() data: IupicsDataField;

  @Input()
  columnName: string;

  quill: Quill;

  displayMobileMenu = false;
  isMobile = false;

  dataContainers: AbstractDataContainerCallout;

  private __fieldValue: any;
  set fieldValue(value: any) {
    this.__fieldValue = value;
    this.updateQuillContent();
  }

  get fieldValue(): any {
    return this.__fieldValue;
  }

  private blurListener: () => void = () => {};

  ngOnInit() {
    super.ngOnInit();
    if (this.cssClass !== undefined) {
      this.cssGrid = this.cssClass;
    }
    this.cssClass = ' ' + this.cssGrid;
  }

  ngAfterViewInit() {
    super.ngAfterViewInit();
    let parentComp = this.DOMParentComponent;
    while (parentComp && !(parentComp instanceof EditTabUiComponent)) {
      parentComp = parentComp.DOMParentComponent;
    }
    if (parentComp instanceof EditTabUiComponent) {
      this.dataContainers = new AbstractDataContainerCallout(parentComp.dataContainers);
    }
    //Add custom Font
    const Font = Quill.import('formats/font') as any;
    Font.whitelist = ['sans-serif', 'serif', 'monospace', 'calibri', 'arial', 'times-new-roman'];
    Quill.register(Font, true);

    this.quill = new Quill(this.editor?.nativeElement, {
      modules: {
        toolbar: this.header?.nativeElement,
      },
      theme: 'snow',
    });
    this.updateQuillContent();

    this.blurListener = this.renderer.listen(this.quill.root, 'blur', (e: FocusEvent) => {
      if (
        isNil(e.relatedTarget) ||
        (!(e.relatedTarget as HTMLElement)?.hasAttribute('data-quill-action') &&
          !(e.relatedTarget as HTMLElement)?.classList.contains('ql-picker-label'))
      ) {
        const newContent = this.quill.root.innerHTML;
        // the stored HTML comes back normalized by the editor: not a change of the user
        if (newContent !== this.fieldValue && newContent !== this.#loadedHtml) {
          this.dataChange(newContent);
        }
      }
    });

    this.quill.update();
    this.updateUI();
  }
  updateReadOnly(dataStored: DataStore, changedColumns?: any): void {
    super.updateReadOnly(dataStored, changedColumns);
    if (this.quill) {
      if (this.isReadOnly) {
        this.quill.disable();
      } else {
        this.quill.enable();
      }
    }
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.blurListener();
  }

  /** HTML of the record as the editor rendered it */
  #loadedHtml: string | null = null;

  updateQuillContent() {
    if (this.quill?.root) {
      // parsed by the editor (inert DOMParser, only known formats kept): writing innerHTML ran the
      // event handlers of the stored HTML (stored XSS)
      const delta = this.quill.clipboard.convert({ html: this.fieldValue || '' });
      this.quill.setContents(delta, 'silent');
      (this.quill.getModule('history') as { clear?: () => void } | undefined)?.clear?.();
      this.#loadedHtml = this.quill.root.innerHTML;
    }
  }
}
